import { useCallback, useEffect, useRef, useState } from "react";

interface UseDragToScrollOptions {
  scrollMultiplier?: number;
  friction?: number;
  minVelocity?: number;
}

export function useDragToScroll(options: UseDragToScrollOptions = {}) {
  const { scrollMultiplier = 2, friction = 0.95, minVelocity = 0.1 } = options;

  const viewportRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0, time: 0 });
  const velocityRef = useRef(0);
  const lastMoveTimeRef = useRef(0);
  const lastPositionRef = useRef(0);
  const animationRef = useRef<number>();

  const animate = useCallback(() => {
    if (!viewportRef.current || Math.abs(velocityRef.current) < minVelocity) {
      return;
    }

    viewportRef.current.scrollLeft += velocityRef.current;
    velocityRef.current *= friction;

    animationRef.current = requestAnimationFrame(animate);
  }, [friction, minVelocity]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!viewportRef.current) return;

    // Cancel any ongoing inertia animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsDragging(true);
    const now = Date.now();
    setDragStart({
      x: e.pageX,
      scrollLeft: viewportRef.current.scrollLeft,
      time: now,
    });

    lastMoveTimeRef.current = now;
    lastPositionRef.current = e.pageX;
    velocityRef.current = 0;

    // Prevent text selection during drag
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !viewportRef.current) return;

      e.preventDefault();
      const x = e.pageX;
      const now = Date.now();
      const walk = (x - dragStart.x) * scrollMultiplier;

      // Calculate velocity for inertia
      const timeDelta = now - lastMoveTimeRef.current;
      if (timeDelta > 0) {
        const positionDelta = x - lastPositionRef.current;
        velocityRef.current = -(positionDelta / timeDelta) * 16; // Negative to match scroll direction
      }

      viewportRef.current.scrollLeft = dragStart.scrollLeft - walk;

      lastMoveTimeRef.current = now;
      lastPositionRef.current = x;
    },
    [isDragging, dragStart.x, dragStart.scrollLeft, scrollMultiplier]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);

    // Start inertia animation
    if (Math.abs(velocityRef.current) > minVelocity) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [animate, minVelocity]);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);

    // Start inertia animation
    if (Math.abs(velocityRef.current) > minVelocity) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [animate, minVelocity]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    viewportRef,
    isDragging,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
    },
    dragStyles: {
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none" as const,
    },
  };
}
